import React, { useState, useEffect } from 'react';
import { Dropdown } from 'react-bootstrap';
import { MdPalette } from 'react-icons/md';
import './ThemeSwitcher.css';

/**
 * Theme Switcher Component
 *
 * Allows users to switch between different theme presets
 * Currently supports: Blue Theme (default)
 * Future themes can be added here
 *
 * Usage: <ThemeSwitcher />
 */

interface Theme {
  id: string;
  name: string;
  description: string;
  primary: {
    dark: string;
    main: string;
    light: string;
    lighter: string;
  };
}

const THEMES: Record<string, Theme> = {
  blue: {
    id: 'blue',
    name: 'Blue Theme',
    description: 'Smart Blue - Default theme',
    primary: {
      dark: '#2c3e50',    // Ink Black
      main: '#486BAD',    // Smart Blue
      light: '#5B8FC7',   // Light Blue
      lighter: '#7BA3D9', // Lighter Blue
    },
  },
  magenta: {
    id: 'magenta',
    name: 'Magenta Bloom',
    description: 'Rich Magenta - Bold & Vibrant',
    primary: {
      dark: '#9D1F3A',    // Deep Magenta
      main: '#D84565',    // Magenta Bloom
      light: '#E5729B',   // Light Magenta
      lighter: '#F0A3BF', // Lighter Magenta
    },
  },
  indigo: {
    id: 'indigo',
    name: 'Space Indigo',
    description: 'Deep Indigo - Modern & Professional',
    primary: {
      dark: '#1A1F2E',    // Deep Space
      main: '#2D3047',    // Space Indigo
      light: '#434960',   // Light Indigo
      lighter: '#6A7590', // Lighter Indigo
    },
  },
};

export default function ThemeSwitcher() {
  const [currentTheme, setCurrentTheme] = useState<string>('blue');
  const [isLoading, setIsLoading] = useState(false);

  // Load theme preference from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('appTheme') || 'blue';
    setCurrentTheme(savedTheme);

    // Apply initial theme colors to CSS variables
    const theme = THEMES[savedTheme];
    if (theme) {
      const hexToRgb = (hex: string) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (result) {
          return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
        }
        return '72, 107, 173';
      };

      document.documentElement.style.setProperty('--theme-primary-dark', theme.primary.dark);
      document.documentElement.style.setProperty('--theme-primary-main', theme.primary.main);
      document.documentElement.style.setProperty('--theme-primary-light', theme.primary.light);
      document.documentElement.style.setProperty('--theme-primary-lighter', theme.primary.lighter);
      document.documentElement.style.setProperty('--theme-primary-main-rgb', hexToRgb(theme.primary.main));
    }
  }, []);

  const handleThemeChange = (themeId: string) => {
    if (themeId === currentTheme) return;

    setIsLoading(true);

    try {
      // Save theme preference
      localStorage.setItem('appTheme', themeId);
      setCurrentTheme(themeId);

      // Apply theme colors to CSS variables (for future use)
      const theme = THEMES[themeId];
      if (theme) {
        // Helper function to convert hex to rgb
        const hexToRgb = (hex: string) => {
          const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
          if (result) {
            return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
          }
          return '72, 107, 173';
        };

        document.documentElement.style.setProperty('--theme-primary-dark', theme.primary.dark);
        document.documentElement.style.setProperty('--theme-primary-main', theme.primary.main);
        document.documentElement.style.setProperty('--theme-primary-light', theme.primary.light);
        document.documentElement.style.setProperty('--theme-primary-lighter', theme.primary.lighter);

        // Also set RGB version for use in rgba() functions
        document.documentElement.style.setProperty('--theme-primary-main-rgb', hexToRgb(theme.primary.main));
      }

      // Dispatch custom event so all components listening to useTheme hook re-render
      window.dispatchEvent(new CustomEvent('themeChanged', { detail: { theme: themeId } }));

      // Show brief feedback
      console.log(`Theme changed to: ${theme?.name}`);
    } finally {
      setIsLoading(false);
    }
  };

  const activeTheme = THEMES[currentTheme];

  return (
    <Dropdown className="theme-switcher-dropdown">
      <Dropdown.Toggle
        variant="link"
        id="theme-switcher-toggle"
        className="theme-switcher-toggle"
        disabled={isLoading}
        title="Switch Theme"
      >
        <MdPalette size={18} />
      </Dropdown.Toggle>

      <Dropdown.Menu align="end" className="theme-switcher-menu">
        <Dropdown.Header className="theme-switcher-header">
          <span>Select Theme</span>
        </Dropdown.Header>
        <Dropdown.Divider className="theme-switcher-divider" />

        {Object.entries(THEMES).map(([themeId, theme]) => (
          <Dropdown.Item
            key={themeId}
            onClick={() => handleThemeChange(themeId)}
            className={`theme-switcher-item ${currentTheme === themeId ? 'active' : ''}`}
            active={currentTheme === themeId}
          >
            <div className="theme-item-content">
              <div className="theme-color-preview">
                <div
                  className="color-dot"
                  style={{ backgroundColor: theme.primary.main }}
                  title={theme.primary.main}
                />
              </div>
              <div className="theme-item-text">
                <div className="theme-item-name">{theme.name}</div>
                <div className="theme-item-description">{theme.description}</div>
              </div>
              {currentTheme === themeId && (
                <div className="theme-item-checkmark">✓</div>
              )}
            </div>
          </Dropdown.Item>
        ))}

        <Dropdown.Divider className="theme-switcher-divider" />
        <Dropdown.Header className="theme-switcher-footer">
          <small>More themes coming soon...</small>
        </Dropdown.Header>
      </Dropdown.Menu>
    </Dropdown>
  );
}
